import type { GenerationModifierForm } from './generation-modifier-form'

/**
 * The modifier dialog lists conditions and effects as rows the user adds, so
 * nothing looks required. A row is shown when its fields hold a value or the
 * user has just added it; removing a row clears its fields, which the input
 * then sends as cleared.
 */
export type ConditionKey =
  | 'salaryWorkTime'
  | 'users'
  | 'location'
  | 'restTypes'
  | 'minDuration'
  | 'maxDuration'
  | 'validFrom'
  | 'validTo'

export type EffectKey = 'award' | 'amount' | 'salaryCode' | 'chainAction'

export const CONDITION_KEYS: ConditionKey[] = [
  'location',
  'restTypes',
  'minDuration',
  'maxDuration',
  'salaryWorkTime',
  'users',
  'validFrom',
  'validTo',
]

export const EFFECT_KEYS: EffectKey[] = [
  'award',
  'amount',
  'salaryCode',
  'chainAction',
]

type Values = GenerationModifierForm

export function conditionIsSet(key: ConditionKey, values: Values): boolean {
  switch (key) {
    case 'salaryWorkTime':
      return !!values.salaryWorkTime
    case 'users':
      return values.users.length > 0
    case 'location':
      return values.zones.length > 0 || values.countries.length > 0
    case 'restTypes':
      return values.restTypes.length > 0
    case 'minDuration':
      return values.minDurationHours !== null
    case 'maxDuration':
      return values.maxDurationHours !== null
    case 'validFrom':
      return !!values.validFrom
    case 'validTo':
      return !!values.validTo
  }
}

export function effectIsSet(key: EffectKey, values: Values): boolean {
  switch (key) {
    case 'award':
      return values.award !== 'none'
    case 'amount':
      return values.amount !== null
    case 'salaryCode':
      return !!values.salaryCode
    case 'chainAction':
      return values.chainAction !== 'none'
  }
}

/** Rest types only mean something on rests; a salary code cannot be set on a
 * modifier for both diets and salary days. */
export function conditionAvailable(key: ConditionKey, values: Values): boolean {
  return key !== 'restTypes' || values.appliesTo !== 'work_period'
}

export function effectAvailable(key: EffectKey, values: Values): boolean {
  return key !== 'salaryCode' || values.kind !== 'both'
}

export function clearedCondition(key: ConditionKey): Partial<Values> {
  switch (key) {
    case 'salaryWorkTime':
      return { salaryWorkTime: '' }
    case 'users':
      return { users: [] }
    case 'location':
      return {
        zones: [],
        countries: [],
        matchMode: 'ends_in',
        locationTest: 'inside',
      }
    case 'restTypes':
      return { restTypes: [] }
    case 'minDuration':
      return { minDurationHours: null }
    case 'maxDuration':
      return { maxDurationHours: null }
    case 'validFrom':
      return { validFrom: null }
    case 'validTo':
      return { validTo: null }
  }
}

export function clearedEffect(key: EffectKey): Partial<Values> {
  switch (key) {
    case 'award':
      return { award: 'none' }
    case 'amount':
      return {
        amount: null,
        perDurationHours: null,
        rounding: 'ceil',
        maxAmount: null,
      }
    case 'salaryCode':
      return { salaryCode: '' }
    case 'chainAction':
      return { chainAction: 'none' }
  }
}

/** What an effect starts as when the user adds it: on work periods a modifier
 * usually takes the diet away, on rests it usually awards one. */
export function addedEffect(key: EffectKey, values: Values): Partial<Values> {
  switch (key) {
    case 'award':
      return values.appliesTo === 'work_period'
        ? { award: 'deny' }
        : { award: 'award', amount: values.amount ?? 1 }
    case 'chainAction':
      return { chainAction: 'break' }
    default:
      return {}
  }
}

/** A rest has no value of its own, so awarding one needs an amount: the amount
 * row stays up while that holds, even if the user never added it. */
export function amountRequired(values: Values): boolean {
  return values.award === 'award' && values.appliesTo !== 'work_period'
}

export function initiallyShown(values: Values): Set<string> {
  return new Set<string>([
    ...CONDITION_KEYS.filter(key => conditionIsSet(key, values)),
    ...EFFECT_KEYS.filter(key => effectIsSet(key, values)),
  ])
}
